"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Plus, Edit, CheckCircle, ArrowLeft, Sparkles } from "lucide-react"
import { Textarea } from "@/components/ui/textarea"
import {
	type JSONSchema,
	SchemaVisualEditor,
	JsonSchemaVisualizer,
	SchemaInferencer,
	JsonValidator,
} from "jsonjoy-builder"
import type { DocumentClassifier } from "@/types"
import { getCSRFToken } from "@/lib/utils"

interface DataStructuresTabProps {
	classification: DocumentClassifier
	onUpdate?: (classification: DocumentClassifier) => void
}

export function DataStructuresTab({ classification, onUpdate }: DataStructuresTabProps) {
	const [isEditing, setIsEditing] = useState(false)
	const [showInferencer, setShowInferencer] = useState(false)
	const [showValidator, setShowValidator] = useState(false)
	const [currentSchema, setCurrentSchema] = useState<JSONSchema>(classification.extraction_schema?.schema)
	const [schemaDescription, setSchemaDescription] = useState("")
	const [isGenerating, setIsGenerating] = useState(false)
	const hasSchema = classification.extraction_schema && Object.keys(classification.extraction_schema).length > 0




	const handleGenerateSchema = async () => {
		setIsGenerating(true)
		try {
			const response = await fetch("/api/generate-schema", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ description: schemaDescription }),
			})

			if (response.ok) {
				const { schema } = await response.json()
				setCurrentSchema({ ...schema, additionalProperties: false })
			} else {
				console.error("Failed to generate schema")
			}
		} catch (error) {
			console.error("Error generating schema:", error)
		} finally {
			setIsGenerating(false)
		}
	}

	const handleSave = () => {
		const schemaWithDefaults = {
			// @ts-ignore
			...currentSchema,
			additionalProperties: false,
		}
		const csrfToken = getCSRFToken()
		fetch(
			"/document_classifier/" + classification.id, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				"X-CSRF-Token": csrfToken || "",

			},
			body: JSON.stringify({ ...classification, extraction_schema: schemaWithDefaults }),
		}).then((response) => {
			if (!response.ok) {
				console.error("Failed to save schema")
			}
		})

		if (onUpdate) {
			onUpdate({
				...classification,
				extraction_schema: schemaWithDefaults,
			})

		}
		setIsEditing(false)
		setSchemaDescription("")
	}

	const handleCancel = () => {
		setCurrentSchema((classification.extraction_schema?.schema as JSONSchema) || {})
		setIsEditing(false)
		setSchemaDescription("")
	}

	const handleEdit = () => {
		setCurrentSchema((classification.extraction_schema?.schema as JSONSchema) || {})
		setSchemaDescription("")
		setIsEditing(true)
	}

	if (isEditing) {
		return (
			<div className="space-y-4">
				<div className="flex items-center gap-2 mb-4">
					<Button variant="ghost" size="sm" onClick={handleCancel}>
						<ArrowLeft className="h-4 w-4 mr-2" />
						Back
					</Button>
					<h3 className="text-lg font-medium">Edit JSON Schema</h3>
				</div>

				<div className="space-y-6">
					<div className="space-y-2 max-h-[400px] flex flex-col">
						<Label htmlFor="schema-description">Describe Your Schema</Label>
						<p className="text-xs text-muted-foreground mb-2">
							Describe what fields you want in natural language, and AI will generate the schema for you.
						</p>
						<Textarea
							id="schema-description"
							className="font-mono text-sm max-h-[400px] overflow-auto"
							value={schemaDescription}
							onChange={(e) => setSchemaDescription(e.target.value)}
							placeholder={`For this document give me a json object with the following fields:\n- field_name_1\n- field_name_2\n- field_name_3`}
							rows={8}
						/>
						<Button
							type="button"
							onClick={handleGenerateSchema}
							disabled={!schemaDescription.trim() || isGenerating}
							className="w-full"
						>
							<Sparkles className="h-4 w-4 mr-2" />
							{isGenerating ? "Generating Schema..." : "Generate Schema from Description"}
						</Button>
					</div>

					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<div className="flex gap-2">
								<Button type="button" variant="outline" size="sm" onClick={() => setShowInferencer(!showInferencer)}>
									<CheckCircle className="h-4 w-4 mr-2" />
									{showInferencer ? "Hide" : "Show"} Inferencer
								</Button>
								<Button type="button" variant="outline" size="sm" onClick={() => setShowValidator(!showValidator)}>
									<CheckCircle className="h-4 w-4 mr-2" />
									{showValidator ? "Hide" : "Show"} Validator
								</Button>
							</div>
						</div>

						<SchemaInferencer
							open={showInferencer}
							onOpenChange={setShowInferencer}
							onSchemaInferred={(inferredSchema) => {
								// @ts-ignore
								setCurrentSchema({ ...inferredSchema, additionalProperties: false })
							}}
						/>

						<JsonValidator open={showValidator} onOpenChange={setShowValidator} schema={currentSchema} />


						<div className="flex flex-col md:flex-row md:gap-4 overflow-hidden">
							<div className="md:w-1/2 max-w-screen md:shrink-0 border rounded-lg md:p-4 h-[600px] overflow-none">
								<h4 className="text-sm font-medium mb-3">Visual Editor</h4>
								<SchemaVisualEditor

									schema={currentSchema}
									onChange={(newSchema) => {
										setCurrentSchema(newSchema)
									}}
								/>
							</div>
							<div className="md:w-1/2   bg-card jsonjoy h-[600px] overflow-auto">
								<h4 className="text-sm  font-medium mb-3">JSON View</h4>
								<JsonSchemaVisualizer
									className="jsonjoy"
									schema={currentSchema}
									onChange={(newSchema) => {
										setCurrentSchema(newSchema)
									}}
								/>
							</div>
						</div>
					</div>

					<div className="flex gap-2 justify-end">
						<Button type="button" variant="outline" onClick={handleCancel}>
							Cancel
						</Button>
						<Button type="button" onClick={handleSave}>
							Save Schema
						</Button>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<p className="text-sm text-muted-foreground">
					{hasSchema ? "JSON schema configured for data extraction" : "Configure a JSON schema for data extraction"}
				</p>
				<Button size="sm" onClick={handleEdit}>
					{hasSchema ? (
						<>
							<Edit className="h-4 w-4 mr-2" />
							Edit Schema
						</>
					) : (
						<>
							<Plus className="h-4 w-4 mr-2" />
							Add Schema
						</>
					)}
				</Button>
			</div>

			{hasSchema ? (
				<Card className="p-4">
					<div className="space-y-2">
						<Label className="text-xs text-muted-foreground">JSON Schema</Label>
						<pre className="p-3 bg-muted rounded-lg text-xs overflow-x-auto max-h-[500px]">

							{classification.extraction_schema ? JSON.stringify(classification.extraction_schema?.schema, null, 2) : ""}
						</pre>
					</div>
				</Card>
			) : (
				<p className="text-sm text-muted-foreground text-center py-8">
					No data structure configured for this classification
				</p>
			)}
		</div>
	)
}

